import { useMemo } from 'react';
import {
  Page,
  Text,
  View,
  Font,
  Image,
  Document,
  PDFViewer,
  StyleSheet,
} from '@react-pdf/renderer';

import { useMediaQuery } from '@mui/material';

import { formatDate, formatPhoneNumber, redirectToPayment } from 'src/utils/utils';

import { STATUS, type Order } from 'src/types/types';

type InvoicePDFProps = {
  order?: Order;
};

export function InvoicePDFViewer({ order }: InvoicePDFProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (isMobile) {
    return <InvoicePdfDocumentAsHtml order={order} />;
  }

  // Calculate total here for button usage
  const total = (order?.totalPrice ?? 0) + (order?.hasDelivery ? (order?.deliveryPrice ?? 0) : 0);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <PDFViewer
        width="100%"
        height={order?.status === STATUS.BETALING ? '90%' : '100%'}
        style={{ border: 'none' }}
      >
        <InvoicePdfDocument order={order} />
      </PDFViewer>

      {order?.status === STATUS.BETALING && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button
            onClick={() => order.revolutOrderId && redirectToPayment(order.revolutOrderId)}
            style={{
              padding: '12px 24px',
              backgroundColor: 'black',
              color: 'white',
              borderRadius: 6,
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#222';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'black';
            }}
          >
            Gå til betaling
          </button>
        </div>
      )}
    </div>
  );
}

function InvoicePdfDocumentAsHtml({ order }: { order?: Order }) {
  if (!order) return <p>Ingen ordre data</p>;

  const total = (order.totalPrice ?? 0) + (order.hasDelivery ? (order.deliveryPrice ?? 0) : 0);
  const moms = total / 5;

  return (
    <div
      style={{
        fontFamily: "'Roboto', sans-serif",
        padding: 30,
        maxWidth: 700,
        margin: 'auto',
        color: '#000',
        fontSize: 12,
        lineHeight: 1.5,
        position: 'relative',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 40 }}>
        <img src="../../../assets/ms-logo.png" alt="Melfar Logo" style={{ height: 40 }} />
        <div style={{ textTransform: 'capitalize', fontWeight: 700, fontSize: 16 }}>
          {getHeader(order)}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 30 }}>
        <div style={{ width: '48%' }}>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>Fra</div>
          <div style={{ fontWeight: '700', marginBottom: 4 }}>Coveen Copu</div>
          <div style={{ marginBottom: 2 }}>Melfar Serviceudlejning ApS</div>
          <div style={{ marginBottom: 4 }}>CVR: 46040686</div>
          <div style={{ marginBottom: 4 }}>Hollændervej 162, 5500 Middelfart</div>
          <div>Tlf: 21 96 23 42</div>
        </div>

        <div style={{ width: '48%' }}>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>Til</div>
          <div style={{ fontWeight: '700', marginBottom: 4 }}>{order.customer?.name}</div>

          {order.customer?.company && order.customer?.companyCvr && (
            <>
              <div style={{ marginBottom: 2 }}>{order.customer.company}</div>
              <div style={{ marginBottom: 4 }}>CVR: {order.customer.companyCvr}</div>
            </>
          )}

          <div style={{ marginBottom: 4 }}>{order.customer?.address}</div>
          {order.customer?.phone && <div>Tlf: {formatPhoneNumber(order.customer.phone)}</div>}
        </div>
      </div>

      {/* Datoer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 40 }}>
        <div style={{ width: '48%' }}>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>
            {order.hasDelivery ? 'Leveringsdato' : 'Afhentningsdato'}
          </div>
          <div>{formatDate(order.startDate)}</div>
        </div>
        <div style={{ width: '48%' }}>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>
            {order.hasDelivery ? 'Afhentningsdato' : 'Tilbageleveringsdato'}
          </div>
          <div>{formatDate(order.endDate)}</div>
        </div>
      </div>

      {/* Produkt tabel */}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: 30,
          fontSize: 12,
        }}
      >
        <thead>
          <tr style={{ borderBottom: '2px solid #000', textAlign: 'left' }}>
            <th style={{ padding: '8px 6px', width: '5%' }}>#</th>
            <th style={{ padding: '8px 6px', width: '50%' }}>Produkt</th>
            <th style={{ padding: '8px 6px', width: '10%', textAlign: 'right' }}>Antal</th>
            <th
              style={{
                padding: '8px 6px',
                width: '15%',
                textAlign: 'right',
                whiteSpace: 'nowrap',
              }}
            >
              Pris pr. stk.
            </th>
            <th
              style={{
                padding: '8px 6px',
                width: '20%',
                textAlign: 'right',
                whiteSpace: 'nowrap',
              }}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {order.orderLines?.map((item, index) => (
            <tr key={item.orderLineId} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '6px' }}>{index + 1}</td>
              <td style={{ padding: '6px' }}>{item.name}</td>
              <td style={{ padding: '6px', textAlign: 'right' }}>{item.quantity}</td>
              <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {formatPrice(item.price)}
              </td>
              <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {formatPrice(item.price * item.quantity)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Total */}
      <div
        style={{
          maxWidth: 300,
          marginLeft: 'auto',
          fontSize: 14,
          fontWeight: '700',
          textAlign: 'right',
        }}
      >
        {order.hasDelivery && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontWeight: '400' }}>Levering</div>
            <div style={{ whiteSpace: 'nowrap', fontWeight: '400' }}>
              {formatPrice(order.deliveryPrice ?? 0)}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontWeight: '400' }}>Inkluderet moms</div>
          <div style={{ whiteSpace: 'nowrap', fontWeight: '400' }}>{formatPrice(moms)}</div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '2px solid #000',
            paddingTop: 6,
          }}
        >
          <div style={{ fontWeight: '700' }}>Total</div>
          <div style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>{formatPrice(total)}</div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #e9ecef', paddingTop: 24, marginTop: 60, fontSize: 11 }}>
        {order.status === STATUS.BETALING && (
          <div style={{ marginTop: 0, marginBottom: 16 }}>
            <button
              onClick={() => order.revolutOrderId && redirectToPayment(order.revolutOrderId)}
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '10px 18px',
                backgroundColor: 'black',
                color: '#fff',
                borderRadius: 6,
                textDecoration: 'none',
                fontWeight: '600',
                boxShadow: '0 2px 6px rgba(0, 123, 255, 0.4)',
                transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'black';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 86, 179, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#007bff';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 123, 255, 0.4)';
              }}
            >
              Gå til betaling
            </button>
          </div>
        )}
        {(order.status === STATUS.TILBUD ||
          order.status === STATUS.BEKRÆFTET ||
          order.status === STATUS.BETALING) && (
          <div style={{ fontWeight: 700, marginBottom: 4 }}>BEMÆRK:</div>
        )}

        <div>{getNotes(order)}</div>
      </div>
    </div>
  );
}

function getHeader(order?: Order) {
  switch (order?.status) {
    case STATUS.TILBUD:
      return 'TILBUD';
    case STATUS.BEKRÆFTET:
      return 'BEKRÆFTET';
    case STATUS.UDE:
      return 'UDLEVERET';
    case STATUS.BETALING:
      return 'FAKTURA';
    case STATUS.AFSLUTTET:
      return 'KVITTERING';
    default:
      return '';
  }
}

function getNotes(order?: Order) {
  switch (order?.status) {
    case STATUS.TILBUD:
      return 'Ønsker du at acceptere tilbuddet, bedes du venligst bekræfte dette ved at besvare denne mail/SMS eller kontakte os direkte.';
    case STATUS.BETALING:
      return 'Det kan tage op til 24 timer, inden betalingen er registreret som gennemført.';
    case STATUS.BEKRÆFTET:
      return `Ved ændringer til ordren bedes I kontakte os hurtigst muligt. Ordren kan nedgraderes/annulleres frem til den ${formatDate(
        order?.startDate && order.startDate - 604800
      )}. `;
    case STATUS.AFSLUTTET:
      return 'Betalingen er modtaget. Tak for denne gang!';
    default:
      return '';
  }
}

Font.register({
  family: 'Roboto',
  // fonts from public folder
  fonts: [{ src: '/fonts/Roboto-Regular.ttf' }, { src: '/fonts/Roboto-Bold.ttf' }],
});

const useStyles = () =>
  useMemo(
    () =>
      StyleSheet.create({
        // layout
        page: {
          fontSize: 9,
          lineHeight: 1.6,
          fontFamily: 'Roboto',
          backgroundColor: '#FFFFFF',
          padding: '40px 24px 120px 24px',
        },
        footer: {
          left: 0,
          right: 0,
          bottom: 0,
          padding: 24,
          margin: 'auto',
          borderTopWidth: 1,
          borderStyle: 'solid',
          position: 'absolute',
          borderColor: '#e9ecef',
        },
        container: { flexDirection: 'row', justifyContent: 'space-between' },
        // margin
        mb4: { marginBottom: 4 },
        mb8: { marginBottom: 8 },
        mb40: { marginBottom: 40 },
        // text
        h3: { fontSize: 16, fontWeight: 700, lineHeight: 1.2 },
        h4: { fontSize: 12, fontWeight: 700 },
        text1: { fontSize: 10 },
        text2: { fontSize: 9 },
        text3: { fontSize: 9, fontWeight: 300, marginBottom: '3px' },
        text1Bold: { fontSize: 10, fontWeight: 700 },
        text2Bold: { fontSize: 9, fontWeight: 700 },

        // table
        table: { display: 'flex', width: '100%' },
        row: {
          padding: '10px 0 8px 0',
          flexDirection: 'row',
          borderBottomWidth: 1,
          borderStyle: 'solid',
          borderColor: '#e9ecef',
        },
        cell_1: { width: '5%' },
        cell_2: { width: '50%' },
        cell_3: { width: '15%', paddingLeft: 32 },
        cell_4: { width: '15%', paddingLeft: 8 },
        cell_5: { width: '15%' },
        noBorder: { paddingTop: '10px', paddingBottom: 0, borderBottomWidth: 0 },
      }),
    []
  );

// Hjælpefunktion til at formatere priser med komma som decimalseparator
function formatPrice(price: number) {
  return price.toFixed(2).replace('.', ',') + ' kr.';
}

type InvoicePdfDocumentProps = {
  order?: Order;
};

function InvoicePdfDocument({ order }: InvoicePdfDocumentProps) {
  const {
    customer,
    deliveryPrice,
    endDate,
    hasDelivery,
    id,
    orderLines,
    startDate,
    status,
    totalPrice,
  } = order ?? {};

  const styles = useStyles();

  const total = totalPrice ? totalPrice + (deliveryPrice ?? 0) : 0;

  const summary = [
    { name: 'Inkluderet moms', value: formatPrice(total / 5) },
    { name: 'Total', value: formatPrice(total), styles: styles.h4 },
  ];

  if (hasDelivery)
    summary.splice(0, 0, { name: 'Levering', value: formatPrice(deliveryPrice ?? 0) });

  const renderHeader = () => (
    <View style={[styles.container, styles.mb40]}>
      <Image source="../../../assets/ms-logo.png" style={{ width: 150 }} />

      <View style={{ alignItems: 'flex-end', flexDirection: 'column' }}>
        <Text style={[styles.h3, styles.mb8, { textTransform: 'capitalize' }]}>
          {getHeader(order)}
        </Text>
      </View>
    </View>
  );

  const renderFooter = () => (
    <View style={[styles.container, styles.footer]} fixed>
      <View style={{ width: '100%' }}>
        {(status === STATUS.TILBUD ||
          status === STATUS.BEKRÆFTET ||
          status === STATUS.BETALING) && (
          <Text style={[styles.text2Bold, styles.mb4]}>BEMÆRK:</Text>
        )}
        <Text style={[styles.text2]}>{getNotes(order)} </Text>
      </View>
    </View>
  );

  const renderBillingInfo = () => (
    <View style={[styles.container, styles.mb40]}>
      {/* Fra */}
      <View style={{ width: '50%' }}>
        <Text style={[styles.text3]}>Fra</Text>
        <Text style={[styles.text1Bold, { marginBottom: 4 }]}>Coveen Copu</Text>
        <Text style={[styles.text2, { marginBottom: 2 }]}>Melfar Serviceudlejning ApS</Text>
        <Text style={[styles.text2, { marginBottom: 4 }]}>CVR: 46040686</Text>
        <Text style={[styles.text2, { marginBottom: 4 }]}>Hollændervej 162, 5500 Middelfart</Text>
        <Text style={[styles.text2]}>Tlf: 21 96 23 42</Text>
      </View>

      {/* Til */}
      <View style={{ width: '50%' }}>
        <Text style={[styles.text3]}>Til</Text>
        <Text style={[styles.text1Bold, { marginBottom: 4 }]}>{customer?.name}</Text>
        {customer?.company && customer?.companyCvr && (
          <>
            <Text style={[styles.text2, { marginBottom: 2 }]}>{customer.company}</Text>
            <Text style={[styles.text2, { marginBottom: 4 }]}>CVR: {customer.companyCvr}</Text>
          </>
        )}
        <Text style={[styles.text2, { marginBottom: 4 }]}>{customer?.address}</Text>
        {customer?.phone && (
          <Text style={[styles.text2]}>Tlf: {formatPhoneNumber(customer.phone)}</Text>
        )}
      </View>
    </View>
  );

  const renderDates = () => (
    <View style={[styles.container, styles.mb40]}>
      <View style={{ width: '50%' }}>
        <Text
          style={[styles.text1Bold, styles.mb4]}
        >{`${hasDelivery ? 'Leveringsdato' : 'Afhentningsdato'}`}</Text>
        <Text style={[styles.text2]}>{formatDate(startDate)}</Text>
      </View>
      <View style={{ width: '50%' }}>
        <Text
          style={[styles.text1Bold, styles.mb4]}
        >{`${hasDelivery ? 'Afhentningsdato' : 'Tilbageleveringsdato'}`}</Text>
        <Text style={[styles.text2]}>{formatDate(endDate)}</Text>
      </View>
    </View>
  );

  const renderTable = () => (
    <View style={styles.table}>
      <View>
        <View style={styles.row}>
          <View style={styles.cell_1}>
            <Text style={[styles.text2Bold]}>#</Text>
          </View>
          <View style={styles.cell_2}>
            <Text style={[styles.text2Bold]}>Produkt</Text>
          </View>
          <View style={styles.cell_3}>
            <Text style={[styles.text2Bold]}>Antal</Text>
          </View>
          <View style={styles.cell_4}>
            <Text style={[styles.text2Bold]}>Pris pr. stk.</Text>
          </View>
          <View style={[styles.cell_5, { textAlign: 'right' }]}>
            <Text style={[styles.text2Bold]}>Total</Text>
          </View>
        </View>
      </View>

      <View>
        {orderLines?.map((item, index) => (
          <View key={item.orderLineId} style={styles.row}>
            <View style={styles.cell_1}>
              <Text>{index + 1}</Text>
            </View>
            <View style={styles.cell_2}>
              <Text style={[styles.text2]}>{item.name}</Text>
            </View>
            <View style={styles.cell_3}>
              <Text style={[styles.text2]}>{item.quantity}</Text>
            </View>
            <View style={styles.cell_4}>
              <Text style={[styles.text2]}>{formatPrice(item.price)}</Text>
            </View>
            <View style={[styles.cell_5, { textAlign: 'right' }]}>
              <Text style={[styles.text2]}>{formatPrice(item.price * item.quantity)}</Text>
            </View>
          </View>
        ))}

        {summary.map((item) => (
          <View key={item.name} style={[styles.row, styles.noBorder]}>
            <View style={styles.cell_1} />
            <View style={styles.cell_2} />
            <View style={styles.cell_3} />
            <View style={styles.cell_4}>
              <Text style={[item.styles ?? styles.text2]}>{item.name}</Text>
            </View>
            <View style={[styles.cell_5, { textAlign: 'right' }]}>
              <Text style={[item.styles ?? styles.text2]}>{item.value}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {renderHeader()}
        {renderBillingInfo()}
        {renderDates()}
        {renderTable()}
        {renderFooter()}
      </Page>
    </Document>
  );
}
